// Unified chat store — the single source of truth for conversations and
// messages, backed by the conversations API and kept live over WebSocket
// (`conversation:{uuid}` topics dispatched here from wsHandler).
//
// A project chat and a DM are the same thing server-side; project chats are
// just conversations of kind 'project'. The `discussion` (project tab / map)
// and `messaging` (org panel) modules keep only their UI state and delegate
// all data to this module.
import { defineStore } from 'pinia'
import { API, HOST } from '@/utils/http'
import { useOrgStore } from '@/stores/org'
import { useUserStore } from '@/stores/user'

const conversationsUrl = (orgId: number | string | null) => `${HOST}/org/${orgId}/conversations`

// Safety timers for the typing indicator: replies are generated server-side,
// so if one fails — or the agent declines to answer — the indicator must
// clear itself rather than spin forever.
const pendingAgentTimers: Record<string, ReturnType<typeof setTimeout>> = {}
const PENDING_AGENT_TIMEOUT_MS = 45000

// Server datetimes are naive UTC — force the Z so Date.parse doesn't read
// them as local time.
const parseTs = (value: string | null | undefined) => (value ? Date.parse(value.endsWith('Z') ? value : `${value}Z`) : 0)

// Outbox: unsent messages persisted in localStorage so a send survives a
// refresh or network drop. Entries are keyed by the message uuid; the server
// is idempotent on that uuid, so resending is always safe.
const OUTBOX_KEY = 'chat_outbox'
const OUTBOX_MAX_AGE_MS = 24 * 60 * 60 * 1000
// A network error or 5xx leaves an entry retryable, and flushOutbox runs on
// every reconnect — without a cap a permanently failing send is re-POSTed on
// every reconnect for a full day.
const OUTBOX_MAX_ATTEMPTS = 5

// The read watermark only has to reach the server eventually; without this,
// every arriving message costs an HTTP request while the tab is open.
const MARK_READ_DEBOUNCE_MS = 1000
const markReadTimers: Record<string, ReturnType<typeof setTimeout>> = {}

// A reconnect refetches the list and every loaded thread. A flapping socket
// must not turn that into a request storm.
const CATCH_UP_MIN_INTERVAL_MS = 5000
let lastCatchUpAll = 0

// Thread trees are rebuilt only when their conversation actually changes.
// `threads` is a parameterized getter, which the store cannot cache, and it is read
// once per render by the feed, the search, the map popup and the map getters.
const threadCache = new Map<string, { version: number, tree: ChatThread[] }>()

function insertOrdered(items: ChatMessage[], message: ChatMessage) {
  let index = items.length
  while (index > 0 && items[index - 1].id > message.id) index -= 1
  items.splice(index, 0, message)
}

function lastDelivered(items: ChatMessage[]) {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (!items[index].pending && !items[index].failed) return items[index]
  }
  return null
}

function buildThreads(items: ChatMessage[]) {
  const roots: ChatThread[] = []
  const byId: Record<string, ChatThread> = {}
  for (const message of items) {
    if (!message.parentId) {
      const root = { ...message, replies: [] }
      byId[message.id] = root
      roots.push(root)
    }
  }
  for (const message of items) {
    if (message.parentId) byId[message.parentId]?.replies.push(message)
  }
  return roots
}

function readOutbox() {
  try {
    const entries: OutboxEntry[] = JSON.parse(localStorage.getItem(OUTBOX_KEY) as string) || []
    return entries.filter((e) => Date.now() - e.ts < OUTBOX_MAX_AGE_MS)
  } catch (e) {
    return []
  }
}

function writeOutbox(entries: OutboxEntry[]) {
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(entries))
  } catch (e) {
    // Storage full/unavailable — the message still lives in the store.
  }
}

function outboxPut(entry: OutboxEntry) {
  writeOutbox([...readOutbox().filter((e) => e.body.uuid !== entry.body.uuid), entry])
}

function outboxRemove(messageUuid: string) {
  writeOutbox(readOutbox().filter((e) => e.body.uuid !== messageUuid))
}

function outboxSetFailed(messageUuid: string, failed: boolean) {
  writeOutbox(readOutbox().map((e) => (e.body.uuid === messageUuid ? { ...e, failed } : e)))
}

// Returns the new attempt count for the entry.
function outboxBumpAttempts(messageUuid: string) {
  let attempts = 0
  writeOutbox(
    readOutbox().map((e) => {
      if (e.body.uuid !== messageUuid) return e
      attempts = (e.attempts || 0) + 1
      return { ...e, attempts }
    })
  )
  return attempts
}

// Local mirror of the server's reaction toggle, for optimistic rendering.
function toggleReactionLocal(reactions: any[], emoji: string, me: any) {
  const list = (reactions || []).map((r: any) => ({
    ...r,
    member_ids: [...(r.member_ids || [])],
    names: [...(r.names || [])],
  }))
  const entry = list.find((r) => r.emoji === emoji)
  if (!entry) {
    return [...list, { emoji, member_ids: [me.member_id], names: [me.display_name || 'You'] }]
  }
  const index = entry.member_ids.indexOf(me.member_id)
  if (index >= 0) {
    entry.member_ids.splice(index, 1)
    entry.names.splice(index, 1)
  } else {
    entry.member_ids.push(me.member_id)
    entry.names.push(me.display_name || 'You')
  }
  return list.filter((r) => r.member_ids.length)
}

// Server message -> the shape the prototype components already consume.
function mapMessage(m: any) {
  const sender = m.sender || {}
  const isAgent = sender.kind === 'agent'
  return {
    id: m.id,
    uuid: m.uuid,
    conversationUuid: m.conversation_uuid,
    parentId: m.parent_id || null,
    author: {
      member_id: isAgent ? `agent:${sender.id}` : sender.id,
      display_name: sender.display_name || 'Unknown',
      avatar_url: sender.avatar_url || null,
      emoji: sender.emoji || null,
      is_agent: isAgent,
      agent_id: isAgent ? sender.id : undefined,
    },
    text: m.text,
    mentions: m.mentions || [],
    attachments: m.attachments || [],
    createdAt: parseTs(m.created_at),
    location: m.location || null,
    geometry: m.geometry || null,
    featureCode: m.feature_code || null,
    resolved: !!m.resolved,
    reactions: m.reactions || [],
    // Any server copy (POST response, WS echo, catch-up fetch) clears the
    // optimistic flags when it merges over a pending entry.
    pending: false,
    failed: false,
  }
}

// Server conversation list item -> store shape. For DMs the peer is the
// participant that isn't the current member (an agent participant wins).
function mapConversation(c: any, myMemberId: number | undefined) {
  const members: any[] = c.members || []
  const agentMember = members.find((m) => m.kind === 'agent')
  const memberPeer = members.find((m) => m.kind === 'member' && m.id !== myMemberId)
  const peerInfo = agentMember || memberPeer
  return {
    uuid: c.uuid,
    kind: c.kind,
    projectId: c.project_id || null,
    peerKind: c.kind === 'dm' ? (agentMember ? 'agent' : 'member') : null,
    peer: peerInfo
      ? {
          member_id: peerInfo.kind === 'agent' ? `agent:${peerInfo.id}` : peerInfo.id,
          display_name: peerInfo.display_name || 'Unknown',
          avatar_url: peerInfo.avatar_url || null,
          emoji: peerInfo.emoji || null,
          is_agent: peerInfo.kind === 'agent',
          agent_id: peerInfo.kind === 'agent' ? peerInfo.id : undefined,
        }
      : null,
    lastMessage: c.last_message ? mapMessage(c.last_message) : null,
    unreadCount: c.unread_count || 0,
    lastAt: parseTs(c.last_message_at) || parseTs(c.created_at),
  }
}

export type ChatMessage = ReturnType<typeof mapMessage>
export type ChatThread = ChatMessage & { replies: ChatMessage[] }
export type ChatConversation = ReturnType<typeof mapConversation>

interface ChatThreadState {
  items: ChatMessage[]
  hasMore: boolean
  loaded: boolean
  version: number
}

interface OutboxEntry {
  orgId: number | string | null
  conversationUuid: string
  body: Record<string, any> & { uuid: string }
  optimistic: ChatMessage
  ts: number
  failed: boolean
  attempts?: number
}

const selectedOrg = () => useOrgStore().selectedOrg
const myMember = () => useUserStore().member

export const useChatStore = defineStore('chat', {
  state: () => ({
    byUuid: {} as Record<string, ChatConversation>, // uuid -> mapped conversation
    listLoaded: false,
    projectConvByProject: {} as Record<string, string>, // String(projectId) -> uuid
    messages: {} as Record<string, ChatThreadState>, // uuid -> { items: [], hasMore: false, loaded: false }
    pendingAgents: {} as Record<string, string | null>, // uuid -> agent display name awaiting a reply
  }),

  getters: {
    conversations: (state): ChatConversation[] =>
      Object.values(state.byUuid).sort((a, b) => b.lastAt - a.lastAt),
    conversation: (state) => (uuid: string): ChatConversation | null => state.byUuid[uuid] || null,
    conversationUuids: (state) => Object.keys(state.byUuid),
    projectUuid: (state) => (projectId: number | string): string | null => state.projectConvByProject[String(projectId)] || null,
    // All messages of a conversation, flat, ascending (DM view).
    flatMessages: (state) => (uuid: string): ChatMessage[] => state.messages[uuid]?.items || [],
    // Root messages with replies nested under their parent (project chat view).
    // Reading `version` keeps this reactive while letting the cache stand.
    threads: (state) => (uuid: string): ChatThread[] => {
      const thread = state.messages[uuid]
      if (!thread) return []
      const cached = threadCache.get(uuid)
      if (cached && cached.version === thread.version) return cached.tree
      const tree = buildThreads(thread.items)
      threadCache.set(uuid, { version: thread.version, tree })
      return tree
    },
    hasMore: (state) => (uuid: string) => state.messages[uuid]?.hasMore || false,
    loaded: (state) => (uuid: string) => state.messages[uuid]?.loaded || false,
    pendingAgent: (state) => (uuid: string) => state.pendingAgents[uuid] || null,
    totalUnread: (state) =>
      Object.values(state.byUuid).reduce((sum, c) => sum + (c.unreadCount || 0), 0),
  },

  actions: {
    // Org switch: everything here is scoped to one org.
    reset() {
      this.byUuid = {}
      this.listLoaded = false
      this.projectConvByProject = {}
      this.messages = {}
      this.pendingAgents = {}
      threadCache.clear()
      // Otherwise a timer from the previous org fires setPendingAgent into
      // the new org's state.
      Object.keys(pendingAgentTimers).forEach((uuid) => {
        clearTimeout(pendingAgentTimers[uuid])
        delete pendingAgentTimers[uuid]
      })
      Object.keys(markReadTimers).forEach((uuid) => {
        clearTimeout(markReadTimers[uuid])
        delete markReadTimers[uuid]
      })
    },
    upsertConversation(conversation: ChatConversation) {
      this.byUuid[conversation.uuid] = {
        ...this.byUuid[conversation.uuid],
        ...conversation,
      }
      if (conversation.kind === 'project' && conversation.projectId) {
        this.projectConvByProject[String(conversation.projectId)] = conversation.uuid
      }
    },
    ensureThread(uuid: string) {
      if (!this.messages[uuid]) {
        this.messages[uuid] = { items: [], hasMore: false, loaded: false, version: 0 }
      }
      return this.messages[uuid]
    },
    appendMessage(uuid: string, message: ChatMessage) {
      const thread = this.messages[uuid]
      if (!thread) return
      thread.version += 1
      const existing = thread.items.findIndex((m) => m.uuid === message.uuid)
      if (existing >= 0) {
        // The merge may swap an optimistic temp id for the real server id, so
        // the entry is re-placed rather than left where it was.
        const merged = { ...thread.items[existing], ...message }
        thread.items.splice(existing, 1)
        insertOrdered(thread.items, merged)
        return
      }
      insertOrdered(thread.items, message)
      const conversation = this.byUuid[uuid]
      if (conversation) {
        conversation.lastMessage = message
        conversation.lastAt = Math.max(conversation.lastAt || 0, message.createdAt)
      }
    },
    // Partial merge by message uuid — for optimistic entries that don't have a
    // stable server id yet (updateMessage matches by id).
    patchMessage(uuid: string, messageUuid: string, patch: Partial<ChatMessage>) {
      const thread = this.messages[uuid]
      if (!thread) return
      const index = thread.items.findIndex((m) => m.uuid === messageUuid)
      if (index >= 0) {
        thread.items[index] = { ...thread.items[index], ...patch }
        thread.version += 1
      }
    },
    updateMessage(uuid: string, message: Partial<ChatMessage> & { id: number }) {
      const thread = this.messages[uuid]
      if (!thread) return
      const index = thread.items.findIndex((m) => m.id === message.id)
      if (index >= 0) {
        thread.items[index] = { ...thread.items[index], ...message }
        thread.version += 1
      }
    },
    removeMessage(uuid: string, messageId: number) {
      const thread = this.messages[uuid]
      if (!thread) return
      thread.items = thread.items.filter((m) => m.id !== messageId && m.parentId !== messageId)
      thread.version += 1
    },
    setUnread(uuid: string, count: number) {
      const conversation = this.byUuid[uuid]
      if (conversation) conversation.unreadCount = count
    },

    async fetchConversations() {
      const orgId = selectedOrg()
      if (!orgId) return
      const myId = myMember()?.member_id
      const response = await API({ method: 'GET', url: conversationsUrl(orgId) })
      for (const item of response.data.conversations) {
        this.upsertConversation(mapConversation(item, myId))
      }
      this.listLoaded = true
    },

    // Project Chat bootstrap: resolve (and lazily create) the project's
    // conversation, then make sure its messages are loaded.
    async ensureProjectConversation(projectId: number | string) {
      let uuid = this.projectConvByProject[String(projectId)]
      if (!uuid) {
        const orgId = selectedOrg()
        const myId = myMember()?.member_id
        const response = await API({
          method: 'GET',
          url: `${conversationsUrl(orgId)}/project/${projectId}`,
        })
        const conversation = mapConversation(response.data, myId)
        this.upsertConversation(conversation)
        uuid = conversation.uuid
      }
      await this.ensureMessages(uuid)
      return uuid
    },

    async openDm({ peerMemberId, peerAgentId }: { peerMemberId?: number | null, peerAgentId?: number | null }) {
      const orgId = selectedOrg()
      const myId = myMember()?.member_id
      const response = await API({
        method: 'POST',
        url: conversationsUrl(orgId),
        data: { peer_member_id: peerMemberId ?? null, peer_agent_id: peerAgentId ?? null },
      })
      const conversation = mapConversation(response.data, myId)
      this.upsertConversation(conversation)
      await this.ensureMessages(conversation.uuid)
      return conversation.uuid as string
    },

    async ensureMessages(uuid: string) {
      if (this.ensureThread(uuid).loaded) {
        await this.catchUp(uuid)
        return
      }
      await this.fetchMessages({ uuid })
    },

    async fetchMessages({ uuid, beforeId = null }: { uuid: string, beforeId?: number | null }) {
      const orgId = selectedOrg()
      this.ensureThread(uuid)
      const params: Record<string, number> = { limit: 50 }
      if (beforeId) params.before_id = beforeId
      const response = await API({
        method: 'GET',
        url: `${conversationsUrl(orgId)}/${uuid}/messages`,
        params,
      })
      const thread = this.messages[uuid]
      // reset() (org switch, logout) ran while the request was in flight.
      if (!thread) return
      const items: ChatMessage[] = response.data.items.map(mapMessage)
      const fetched = new Set(thread.items.map((m) => m.uuid))
      thread.items = [...items.filter((m) => !fetched.has(m.uuid)), ...thread.items]
      thread.hasMore = response.data.has_more
      thread.loaded = true
      thread.version += 1
      // Re-render unsent messages that survived a reload in the outbox. Skip
      // uuids the fetch already returned — those sends did reach the server.
      const known = new Set(thread.items.map((m) => m.uuid))
      for (const entry of readOutbox()) {
        if (entry.conversationUuid !== uuid || known.has(entry.body.uuid)) continue
        this.appendMessage(uuid, { ...entry.optimistic, pending: !entry.failed, failed: !!entry.failed })
      }
    },

    // Reconnect catch-up: fetch everything newer than the last message we hold.
    async catchUp(uuid: string) {
      const thread = this.messages[uuid]
      if (!thread?.loaded) return
      const orgId = selectedOrg()
      // Pending/failed entries carry huge temp ids — anchor on the last
      // delivered message or after_id would skip everything.
      const delivered = lastDelivered(thread.items)
      const latestId = delivered ? delivered.id : 0
      const response = await API({
        method: 'GET',
        url: `${conversationsUrl(orgId)}/${uuid}/messages`,
        params: { after_id: latestId, limit: 200 },
      })
      for (const item of response.data.items) {
        this.appendMessage(uuid, mapMessage(item))
      }
    },

    // Called on every WS (re)connect: refresh the list and fill gaps in every
    // loaded thread — chunks broadcast while the socket was down are lost, and
    // this is the recovery path.
    async catchUpAll() {
      const now = Date.now()
      if (now - lastCatchUpAll < CATCH_UP_MIN_INTERVAL_MS) return
      lastCatchUpAll = now
      await this.flushOutbox().catch(() => {})
      await this.fetchConversations().catch(() => {})
      await Promise.all(
        Object.keys(this.messages)
          .filter((uuid) => this.messages[uuid].loaded)
          .map((uuid) => this.catchUp(uuid).catch(() => {}))
      )
    },

    async sendMessage(
      { uuid, text, mentions, attachments, location, geometry, featureCode, parentId }:
      { uuid: string, text?: string, mentions?: any[], attachments?: any[], location?: any, geometry?: any, featureCode?: string | null, parentId?: number | null }
    ) {
      const orgId = selectedOrg()
      // Images go up as a bare resource reference — the server resolves the
      // URLs from the resource table and rejects anything else.
      const requestAttachments = (attachments || []).map((a) =>
        a.type === 'image'
          ? {
              type: 'image',
              resource_uuid: a.resource_uuid,
              filename: a.filename || '',
              mime: a.mime || '',
              sizebytes: a.sizebytes || 0,
            }
          : a
      )
      const body = {
        uuid: crypto.randomUUID(),
        text: text || '',
        parent_id: parentId ?? null,
        mentions: mentions || [],
        attachments: requestAttachments,
        location: location ?? null,
        geometry: geometry ?? null,
        feature_code: featureCode ?? null,
      }
      // Render immediately with a temp id — Date.now() sorts after every real
      // server id and stays monotonic across consecutive sends. The POST
      // response (or WS echo) merges over this entry by uuid.
      const me = myMember() || {}
      const optimistic = {
        id: Date.now(),
        uuid: body.uuid,
        conversationUuid: uuid,
        parentId: body.parent_id,
        author: {
          member_id: me.member_id,
          display_name: me.display_name || 'You',
          avatar_url: me.avatar_url || null,
          emoji: me.emoji || null,
          is_agent: false,
        },
        text: body.text,
        mentions: body.mentions,
        attachments: attachments || [],
        createdAt: Date.now(),
        location: body.location,
        geometry: body.geometry,
        featureCode: body.feature_code,
        resolved: false,
        reactions: [],
        pending: true,
        failed: false,
      } as ChatMessage
      this.ensureThread(uuid)
      this.appendMessage(uuid, optimistic)
      const entry = { orgId, conversationUuid: uuid, body, optimistic, ts: Date.now(), failed: false }
      outboxPut(entry)
      return this.postOutboxEntry(entry)
    },

    // Deliver one outbox entry. Shared by sendMessage, flushOutbox and
    // retryMessage — the server dedups by message uuid, so replays are safe.
    async postOutboxEntry(entry: OutboxEntry) {
      try {
        const response = await API({
          method: 'POST',
          url: `${conversationsUrl(entry.orgId)}/${entry.conversationUuid}/messages`,
          data: entry.body,
        })
        const message = mapMessage(response.data)
        this.ensureThread(entry.conversationUuid)
        this.appendMessage(entry.conversationUuid, message)
        outboxRemove(entry.body.uuid)
        return message
      } catch (error: any) {
        const status = error.response?.status
        // A 4xx won't succeed on replay; a network error or 5xx might, but only
        // for so long — past the cap it is handed to the user as failed too.
        const terminal = (status && status < 500)
          || outboxBumpAttempts(entry.body.uuid) >= OUTBOX_MAX_ATTEMPTS
        if (terminal) {
          outboxSetFailed(entry.body.uuid, true)
          this.patchMessage(entry.conversationUuid, entry.body.uuid, { pending: false, failed: true })
        }
        throw error
      }
    },

    // Resend everything still unsent for this org — runs on every WS
    // (re)connect via catchUpAll.
    async flushOutbox() {
      const orgId = selectedOrg()
      for (const entry of readOutbox()) {
        if (entry.orgId !== orgId || entry.failed) continue
        await this.postOutboxEntry(entry).catch(() => {})
      }
    },

    async retryMessage({ uuid, messageUuid }: { uuid: string, messageUuid: string }) {
      const entry = readOutbox().find((e) => e.body.uuid === messageUuid)
      if (!entry) return
      outboxSetFailed(messageUuid, false)
      // A manual retry starts the attempt budget over.
      writeOutbox(readOutbox().map((e) => (e.body.uuid === messageUuid ? { ...e, attempts: 0 } : e)))
      this.patchMessage(uuid, messageUuid, { pending: true, failed: false })
      await this.postOutboxEntry(entry).catch(() => {})
    },

    discardMessage({ uuid, messageUuid }: { uuid: string, messageUuid: string }) {
      outboxRemove(messageUuid)
      const item = this.messages[uuid]?.items.find((m) => m.uuid === messageUuid)
      if (item) this.removeMessage(uuid, item.id)
    },

    // Agent replies are triggered server-side (mentions, agent DMs, auto-reply
    // agents). The UI only shows the typing indicator until the agent's
    // message arrives over WS.
    notePendingAgent({ uuid, name }: { uuid: string, name?: string | null }) {
      this.pendingAgents[uuid] = name || 'Agent'
      clearTimeout(pendingAgentTimers[uuid])
      pendingAgentTimers[uuid] = setTimeout(() => {
        delete pendingAgentTimers[uuid]
        this.pendingAgents[uuid] = null
      }, PENDING_AGENT_TIMEOUT_MS)
    },

    // AI run stats for an agent message (super-admin only; server re-checks).
    async fetchMessageDebug({ uuid, messageId }: { uuid: string, messageId: number }) {
      const orgId = selectedOrg()
      const response = await API({
        method: 'GET',
        url: `${conversationsUrl(orgId)}/${uuid}/messages/${messageId}/debug`,
      })
      return response.data.meta
    },

    markRead(uuid: string) {
      const thread = this.messages[uuid]
      if (!thread?.items.length) return
      // The watermark must be a real server id, never an optimistic temp id.
      const lastId = lastDelivered(thread.items)?.id
      if (!lastId) return
      this.setUnread(uuid, 0)
      const orgId = selectedOrg()
      // The counter clears locally straight away; only the POST is coalesced,
      // so a burst of arrivals costs one request instead of one each.
      clearTimeout(markReadTimers[uuid])
      markReadTimers[uuid] = setTimeout(() => {
        delete markReadTimers[uuid]
        API({
          method: 'POST',
          url: `${conversationsUrl(orgId)}/${uuid}/read`,
          data: { last_read_message_id: lastId },
        }).catch(() => {})
      }, MARK_READ_DEBOUNCE_MS)
    },

    async toggleResolved({ uuid, messageId }: { uuid: string, messageId: number }) {
      const orgId = selectedOrg()
      const response = await API({
        method: 'POST',
        url: `${conversationsUrl(orgId)}/${uuid}/messages/${messageId}/resolve`,
      })
      this.updateMessage(uuid, mapMessage(response.data))
    },

    async toggleReaction({ uuid, messageId, emoji }: { uuid: string, messageId: number, emoji: string }) {
      const message = this.messages[uuid]?.items.find((m) => m.id === messageId)
      // Undelivered messages only have a temp id the server doesn't know.
      if (!message || message.pending || message.failed) return
      // Toggle locally right away; the POST response (and the WS echo) then
      // merges the server truth over it, or we roll back on failure. The
      // endpoint is a toggle, so no replay queue here — a replay could invert
      // a state the server already applied.
      const me = myMember() || {}
      const previous = message.reactions
      this.updateMessage(uuid, { id: messageId, reactions: toggleReactionLocal(previous, emoji, me) })
      const orgId = selectedOrg()
      try {
        const response = await API({
          method: 'POST',
          url: `${conversationsUrl(orgId)}/${uuid}/messages/${messageId}/reactions`,
          data: { emoji },
        })
        this.updateMessage(uuid, mapMessage(response.data))
      } catch (error) {
        this.updateMessage(uuid, { id: messageId, reactions: previous })
        throw error
      }
    },

    async deleteMessage({ uuid, messageId }: { uuid: string, messageId: number }) {
      const orgId = selectedOrg()
      await API({
        method: 'DELETE',
        url: `${conversationsUrl(orgId)}/${uuid}/messages/${messageId}`,
      })
      this.removeMessage(uuid, messageId)
    },

    // WS events from wsHandler. `conversation:{uuid}` events carry messages;
    // `member:{id}` events announce new conversations.
    onWsEvent({ topic, event, data }: { topic: string, event: string, data: any }) {
      if (topic.startsWith('member:')) {
        if (event === 'conversation_created') this.fetchConversations().catch(() => {})
        return
      }
      const uuid = topic.slice('conversation:'.length)
      if (event === 'message_created') {
        const message = mapMessage(data)
        this.ensureThread(uuid)
        this.appendMessage(uuid, message)
        if (data.sender?.kind === 'agent') {
          clearTimeout(pendingAgentTimers[uuid])
          delete pendingAgentTimers[uuid]
          this.pendingAgents[uuid] = null
        }
        const conversation = this.byUuid[uuid]
        const myId = myMember()?.member_id
        if (conversation && message.author.member_id !== myId) {
          this.setUnread(uuid, (conversation.unreadCount || 0) + 1)
        }
      } else if (event === 'message_updated') {
        this.updateMessage(uuid, mapMessage(data))
      } else if (event === 'message_deleted') {
        this.removeMessage(uuid, data.id)
      }
    },
  },
})
